import { authSuccess, logOut } from "./authSlice";
import {
  setForecastsParams,
  setStockLinesParams,
  setStockOnHandParams,
  setWarehouseCostingParams,
  setWarehouseCostingRangeParams,
  updateForecastsParams,
  updateStandardCostParams,
  updateStockLocationParams,
  updateStockZoneParams,
  updateWarehouseCostingParams,
  updateWarehouseCostingRangesParams,
  updateStockReplenishmentsParams,
  updatePicklistsParams,
  updateStockPacksParams,
  updateSitesParams,
  updateWorkCentersParams,
} from "./warehouseSlice";
import {
  updatePeopleParams,
  updateBomsParams,
  updateManufacturingOrderParams,
} from "./productionSlice";
import { updateUsersParams } from "./userSlice";
import { updateCoManufacturingOrderParams } from "./manufacturingOrderSlice";
import {
  setProductAliasParams,
  setProductPlansParams,
  setProductsParams,
  updatePackParams,
  updateProductAliasParams,
  updateProductCategoryParams,
  updateProductPlanParams,
  updateProductsParams,
  updateProductTagParams,
} from "./productSlice";
import { setTransactionsParams, updateTransactionReasonParams } from "./reportSlice";
import {
  setWarehousesParams,
  setSuppliersParams,
  updateWarehousesParams,
  updateSuppliersParams,
} from "./supplierSlice";
import {
  updateCustomerSegmentsParams,
  updateCustomersParams,
  updateCustomerTagsParams,
} from "./customerSlice";
import {
  updateASNListParams,
  updateConsignmentsParams,
  updateDeliveryParams,
  updateShipmentsV1Params,
  updateEquipmentTypesParams,
  updateEquipmentsParams,
  updateShipmentLegsParams,
} from "./shipmentSlice";
import { updateFilesParams } from "./fileSlice";
import { updatePlannedOrdersParams } from "./plannerSlice";
import { updatePOWatchlistLateParams, updatePurchaseOrdersParams } from "./purchaseOrderSlice";
import {
  updatePurchaseAllocationsParams,
  updateTransferAllocationsParams,
  updateTransferOrderLinesParams,
  updateTransferOrdersParams,
} from "./orderSlice";
import { updateReceiptAdjustmentsParams, updateReceiptsParams } from "./receiptSlice";
import { updateSettings } from "./miscSlice";
import {
  updateChartsAccountParams,
  updateJournalLinesParams,
  updatePaymentTermsParams,
} from "./accountSlice";
import {
  updateProductTaxClassificationParams,
  updateTaxCategoryParams,
  updateTaxParams,
} from "./taxSlice";
import {
  updateSyncHistoryParams,
  updateShopifyStoresParams,
  updateShopifyCatalogueParams,
  updateShopifyLinkedProductsParams,
} from "./integrationSlice";
import {
  updateBillsParams,
  updateSupplierCreditNoteParams,
  updatePrePaymentParams,
  updateSaleInvoicesParams,
} from "./invoiceSlice";
import { apiSlice } from "../apiSlice/apiSlice";
import { updateUnitOfMeasureParams } from "./unitOfMeasureSlice";
import {
  updateSalesRepsParams,
  updateQuoteParams,
  updateSalesOrderParams,
  updateCustomerReturnParams,
} from "./salesSlice";
import { updatePricelistParams } from "./pricingSlice";
import { updatePromosParams } from "./promoSlice";

/**
 * Updates pagination parameters for various tables in the application.
 * This function dynamically sets items-per-page limits based on provided props,
 * and dispatches only necessary updates to the Redux store, reducing unnecessary state updates.
 *
 * @param {Object} props - Object containing various pagination limits.
 * @param {function} dispatch - Redux dispatch function to update store state.
 */
// Mapping of property keys to their corresponding dispatch actions
export const paramsMap = {
  asnList_items_per_page: updateASNListParams,
  files_items_per_page: updateFilesParams,
  international_shipments_items_per_page: updateConsignmentsParams,
  inventory_stock_line_items_per_page: setStockLinesParams,
  inventory_stock_summary_items_per_page: setStockOnHandParams,
  inventory_transactions_items_per_page: setTransactionsParams,
  master_forecast_items_per_page: setForecastsParams,
  master_product_alias_items_per_page: setProductAliasParams,
  master_product_plan_items_per_page: setProductPlansParams,
  master_products_items_per_page: setProductsParams,
  master_stock_on_hand_items_per_page: setStockOnHandParams,
  master_suppliers_items_per_page: setSuppliersParams,
  master_warehouse_costing_items_per_page: setWarehouseCostingParams,
  master_warehouse_costing_range_items_per_page: setWarehouseCostingRangeParams,
  master_warehouses_items_per_page: setWarehousesParams,
  planned_orders_items_per_page: updatePlannedOrdersParams,
  po_items_per_page: updatePurchaseOrdersParams,
  po_watchlist_items_per_page: updatePOWatchlistLateParams,
  products_items_per_page: updateProductsParams,
  product_category_items_per_page: updateProductCategoryParams,
  product_tag_items_per_page: updateProductTagParams,
  product_alias_items_per_page: updateProductAliasParams,
  product_plan_items_per_page: updateProductPlanParams,
  purchase_allocations_items_per_page: updatePurchaseAllocationsParams,
  receipts_items_per_page: updateReceiptsParams,
  transfer_allocations_items_per_page: updateTransferAllocationsParams,
  transfer_order_lines_items_per_page: updateTransferOrderLinesParams,
  transfer_orders_items_per_page: updateTransferOrdersParams,
  warehouse_items_per_page: updateWarehousesParams,
  supplier_items_per_page: updateSuppliersParams,
  customer_items_per_page: updateCustomersParams,
  pack_items_per_page: updatePackParams,
  warehouse_costing_range_items_per_page: updateWarehouseCostingRangesParams,
  forecast_items_per_page: updateForecastsParams,
  warehouse_costing_items_per_page: updateWarehouseCostingParams,
  stock_zone_items_per_page: updateStockZoneParams,
  stock_location_items_per_page: updateStockLocationParams,
  transaction_reason_items_per_page: updateTransactionReasonParams,
  payment_term_items_per_page: updatePaymentTermsParams,
  journal_line_items_per_page: updateJournalLinesParams,
  standard_cost_items_per_page: updateStandardCostParams,
  bill_items_per_page: updateBillsParams,
  sale_invoice_items_per_page: updateSaleInvoicesParams,
  tax_rate_items_per_page: updateTaxParams,
  charts_account_items_per_page: updateChartsAccountParams,
  receipt_adjustment_items_per_page: updateReceiptAdjustmentsParams,
  sync_history_items_per_page: updateSyncHistoryParams,
  shopify_store_items_per_page: updateShopifyStoresParams,
  shopify_catalogue_items_per_page: updateShopifyCatalogueParams,
  shopify_linked_product_items_per_page: updateShopifyLinkedProductsParams,
  supplier_credit_note_items_per_page: updateSupplierCreditNoteParams,
  pre_payment_items_per_page: updatePrePaymentParams,
  stock_replenishment_items_per_page: updateStockReplenishmentsParams,
  picklist_items_per_page: updatePicklistsParams,
  tax_category_items_per_page: updateTaxCategoryParams,
  customer_tag_items_per_page: updateCustomerTagsParams,
  pricelist_items_per_page: updatePricelistParams,
  product_tax_classification_items_per_page: updateProductTaxClassificationParams,
  customer_segment_items_per_page: updateCustomerSegmentsParams,
  unit_of_measure_items_per_page: updateUnitOfMeasureParams,
  sales_rep_items_per_page: updateSalesRepsParams,
  promo_items_per_page: updatePromosParams,
  quote_items_per_page: updateQuoteParams,
  order_items_per_page: updateSalesOrderParams,
  stock_pack_items_per_page: updateStockPacksParams,
  delivery_items_per_page: updateDeliveryParams,
  shipmentV1_items_per_page: updateShipmentsV1Params,
  asn_items_per_page: updateASNListParams,
  equipment_type_items_per_page: updateEquipmentTypesParams,
  equipment_items_per_page: updateEquipmentsParams,
  shipment_leg_items_per_page: updateShipmentLegsParams,
  customer_return_items_per_page: updateCustomerReturnParams,
  site_items_per_page: updateSitesParams,
  work_center_items_per_page: updateWorkCentersParams,
  people_items_per_page: updatePeopleParams,
  users_items_per_page: updateUsersParams,
  boms_items_per_page: updateBomsParams,
  manufacturing_order_items_per_page: updateManufacturingOrderParams,
  co_manufacturing_order_items_per_page: updateCoManufacturingOrderParams,
};

const updateParams = (props, dispatch) => {
  // Loop through the paramsMap to dynamically dispatch updates
  Object.entries(paramsMap).forEach(([propKey, action]) => {
    const limit = props[propKey];
    if (limit) {
      dispatch(action({ limit }));
    }
  });
};

const updatePrecision = (precision, dispatch) => {
  if (precision) {
    dispatch(updateSettings({ precision }));
  }
};

/** Clear local auth state after a failed login/autologin attempt (no cross-tab signal). */
export const authenticationFailed = () => dispatch => {
  dispatch(logOut());
};

export const authenticationSuccess = data => dispatch => {
  localStorage.setItem("authIn", Date.now());
  dispatch(authSuccess(data));
  if (data.limit) updateParams(data.limit, dispatch);
  updatePrecision(data.precision, dispatch);
};

export const logOutUser = () => dispatch => {
  localStorage.setItem("authOut", Date.now());
  dispatch(logOut());

  //  Wipe RTK Query cache
  dispatch(apiSlice.util.resetApiState());
};

export const authOnSuccess = data => dispatch => {
  dispatch(authSuccess(data));
  if (data.limit) updateParams(data.limit, dispatch);
  updatePrecision(data.precision, dispatch);
};
